import React from 'react';
import { ChevronLeft, ChevronRight, PlayCircle } from 'lucide-react';

/**
 * VideoPlayer Component
 * Updated to support vertical video aspect ratios via props.
 */
const VideoPlayer = ({ 
  videoText, 
  videoUrl, 
  videoIndex, 
  totalVideos, 
  onNext, 
  onPrev,
  aspectRatio = "aspect-video" // Default to 16:9, but overridable
}) => {
  
  const isYouTube = videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
  const isVimeo = videoUrl && videoUrl.includes('vimeo.com');
  const isDirectFile = videoUrl && (videoUrl.endsWith('.mp4') || videoUrl.endsWith('.webm'));

  const getEmbedUrl = (url) => {
      if (isYouTube) {
          const videoId = url.split('v=')[1] || url.split('/').pop();
          // Added 'controls=0' to make it look cleaner in the overlay
          return `https://www.youtube.com/embed/${videoId}?rel=0&controls=1`;
      }
      if (isVimeo) {
          const videoId = url.split('/').pop();
          return `https://player.vimeo.com/video/${videoId}`;
      }
      return url;
  };

  return (
    <div className="animate-fade-in w-full max-w-4xl mx-auto">
      
      {/* VIDEO CONTAINER with Dynamic Aspect Ratio */}
      <div className={`${aspectRatio} w-full bg-black rounded-2xl shadow-2xl overflow-hidden mb-6 relative group border border-gray-800`}>
        
        {videoUrl ? (
            isDirectFile ? (
                <video 
                    controls 
                    className="w-full h-full object-cover" 
                    src={videoUrl}
                >
                    Your browser does not support the video tag.
                </video>
            ) : (
                <iframe 
                    src={getEmbedUrl(videoUrl)} 
                    title="Course Video"
                    className="w-full h-full" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                />
            )
        ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 text-white p-8 text-center">
                <PlayCircle size={64} className="mb-4 text-gray-600 group-hover:text-blue-500 group-hover:scale-110 transition-all duration-300" />
                <h3 className="text-xl font-bold mb-2 text-gray-300">Placeholder Content</h3>
                <p className="text-lg font-medium text-gray-500">{videoText || "Video Content Loading..."}</p>
                <div className="mt-4 px-3 py-1 bg-gray-800 rounded text-xs font-mono text-gray-500 border border-gray-700">
                    ID: vid_{videoIndex + 1}
                </div>
            </div>
        )}
      </div>

      {/* Hide Controls if we are in the Overlay (Single Video Mode) */}
      {/* Logic: If totalVideos is 1, we assume it's an overlay or single clip */}
      {totalVideos > 1 && (
        <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <button
            onClick={onPrev}
            className="flex items-center text-gray-600 font-bold py-2.5 px-5 rounded-lg hover:bg-gray-100 transition-colors"
            >
            <ChevronLeft className="w-5 h-5 mr-2" />
            {videoIndex === 0 ? 'Back to Hub' : 'Previous'}
            </button>
            
            <div className="text-center">
                <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Lesson Progress</span>
                <span className="text-gray-800 font-black">
                {videoIndex + 1} <span className="text-gray-400 font-medium">/</span> {totalVideos}
                </span>
            </div>
            
            <button
            onClick={onNext}
            className="flex items-center bg-blue-600 text-white font-bold py-2.5 px-6 rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg transition-all active:scale-95"
            >
            {videoIndex === totalVideos - 1 ? 'Start Exercise' : 'Next Video'}
            <ChevronRight className="w-5 h-5 ml-2" />
            </button>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;